import { ChangeDetectionStrategy, Component, inject, input, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ProgressSpinner } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { firstValueFrom } from 'rxjs';
import { ToastService } from '@workspace-app/shared/services/toast.service';
import { CertificateSharingService } from '../certificate-sharing.service';
import { CertificateSharingKind, ShareableOrganisation } from '../certificate-sharing.models';

/**
 * Picker for the organisations the caller may share/download certificates for (scoped by the API):
 * loads the list for the given kind, shows the certificate count and the assigned GL per entry,
 * emits the chosen organisation. Used by the share dialog and the per-organisation download block.
 */
@Component({
  selector: 'app-shareable-organisation-select',
  standalone: true,
  imports: [FormsModule, ProgressSpinner, SelectModule],
  template: `
    @if (loading()) {
      <div class="loading-inline">
        <p-progressspinner strokeWidth="4" [style]="{ width: '20px', height: '20px' }" />
        <span>Loading organisations...</span>
      </div>
    } @else {
      <p-select
        [inputId]="inputId()"
        [options]="organisations()"
        [ngModel]="selectedOrganisationId()"
        (ngModelChange)="onChange($event)"
        optionValue="organisationId"
        [filter]="true"
        filterBy="name,assignedGlName"
        placeholder="Choose an organisation"
        [showClear]="true"
        styleClass="w-full"
        [appendTo]="'body'"
        [attr.data-testid]="testId()">
        <ng-template let-organisation #item>
          <div class="flex flex-column">
            <span>{{ organisationLabel(organisation) }}</span>
            @if (organisation.assignedGlName) {
              <small class="text-500">GL: {{ organisation.assignedGlName }}</small>
            }
          </div>
        </ng-template>
        <ng-template let-organisation #selectedItem>
          <span>{{ organisationLabel(organisation) }}</span>
        </ng-template>
      </p-select>
      @if (organisations().length === 0) {
        <small class="text-500 block mt-1">No organisation with current certificates is available to you.</small>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    .loading-inline {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      color: var(--text-color-secondary);
    }
  `,
})
export class ShareableOrganisationSelectComponent implements OnInit {
  private toastService = inject(ToastService);
  private certificateSharingService = inject(CertificateSharingService);

  kind = input.required<CertificateSharingKind>();
  inputId = input('shareable-organisation');
  testId = input('shareable-organisation-select');
  selectionChange = output<ShareableOrganisation | null>();

  organisations = signal<ShareableOrganisation[]>([]);
  loading = signal(true);
  selectedOrganisationId = signal<string | null>(null);

  ngOnInit(): void {
    this.load();
  }

  organisationLabel(organisation: ShareableOrganisation): string {
    const unit = organisation.certificateCount === 1 ? 'certificate' : 'certificates';
    return `${organisation.name} (${organisation.certificateCount} ${unit})`;
  }

  onChange(organisationId: string | null): void {
    this.selectedOrganisationId.set(organisationId);
    this.selectionChange.emit(this.organisations().find((o) => o.organisationId === organisationId) ?? null);
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    try {
      this.organisations.set(await firstValueFrom(this.certificateSharingService.getShareableOrganisations(this.kind())));
    } catch (error) {
      const message = (error as { error?: { message?: string } })?.error?.message;
      this.toastService.displayErrorMessage(message || 'Failed to load organisations');
    } finally {
      this.loading.set(false);
    }
  }
}
